import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LineChart, Line,
} from "recharts";
import {
  BarChart3, TrendingUp, Package, Clock, Star, XCircle, CheckCircle2,
  UtensilsCrossed, History, Search, X, Pencil, Trash2,
  ToggleLeft, ToggleRight, ChefHat, BedDouble, Plus, Image, RefreshCw,
} from "lucide-react";
import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";
import "./RoomServiceManagerDashboard.css";
import NewOrderFlow from "./NewOrderFlow";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:5050";
const CATEGORIES = ["Breakfast", "Main", "Snacks", "Drinks", "Desserts", "Other"];

interface MenuItem {
  id: number; name: string; category: string; price: number;
  description?: string; available: boolean; ingredients: string[];
  unit: string; image_url?: string; available_from?: string; available_to?: string;
}
interface Order {
  id: number; room_number: string; guest_name?: string;
  items: { name: string; qty: number; price: number }[];
  status: string; total: number; notes?: string; created_at: string;
}
interface Analytics {
  topItems: { name: string; count: number; revenue: number }[];
  revenueByCategory: { category: string; revenue: number }[];
  peakHours: { hour: number; count: number }[];
  avgDeliveryTime: number | null;
  cancellationRate: number;
  dailyRevenue: { date: string; revenue: number }[];
  totalRevenue7d: number;
  totalOrders7d: number;
}

const STATUS_META: Record<string, { label: string; color: string; bg: string }> = {
  pending:   { label: "Pending",   color: "#fbbf24", bg: "rgba(251,191,36,0.12)" },
  preparing: { label: "Preparing", color: "#6366f1", bg: "rgba(99,102,241,0.12)" },
  delivered: { label: "Delivered", color: "#34d399", bg: "rgba(52,211,153,0.12)" },
  cancelled: { label: "Cancelled", color: "#f87171", bg: "rgba(239,68,68,0.1)"  },
};

function fmt(iso: string) {
  return new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
}
function todayISO() { return new Date().toISOString().slice(0, 10); }

export default function RoomServiceManagerDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "analytics";
  const setTab = (t: string) => setSearchParams({ tab: t });

  const token = localStorage.getItem("ai2_token");
  const headers = { Authorization: `Bearer ${token}` };

  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const [menu, setMenu]           = useState<MenuItem[]>([]);
  const [menuSearch, setMenuSearch] = useState("");
  const [menuCat, setMenuCat]     = useState("All");
  const [editingItem, setEditingItem] = useState<Partial<MenuItem> | null>(null);
  const [editMode, setEditMode]   = useState<"create" | "edit">("create");
  const [ingredientInput, setIngredientInput] = useState("");
  const [menuError, setMenuError] = useState("");

  const [historyDate, setHistoryDate]     = useState(todayISO());
  const [historySearch, setHistorySearch] = useState("");
  const [history, setHistory]             = useState<Order[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [orders, setOrders]               = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orderFilter, setOrderFilter]     = useState("all");

  /* ── Load data ── */
  const loadOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/roomservice/orders`, { headers });
      setOrders(data || []);
    } finally { setOrdersLoading(false); }
  }, []);

  const updateOrderStatus = async (id: number, status: string) => {
    await axios.put(`${API}/api/roomservice/orders/${id}/status`, { status }, { headers });
    loadOrders();
  };

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/roomservice/analytics`, { headers });
      setAnalytics(data);
    } finally { setAnalyticsLoading(false); }
  }, []);

  const loadMenu = useCallback(async () => {
    const { data } = await axios.get(`${API}/api/roomservice/menu`, { headers });
    setMenu(data || []);
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/roomservice/orders?date=${historyDate}`, { headers });
      setHistory(data || []);
    } finally { setHistoryLoading(false); }
  }, [historyDate]);

  useEffect(() => { if (tab === "analytics") loadAnalytics(); }, [tab]);
  useEffect(() => { if (tab === "menu") loadMenu(); }, [tab]);
  useEffect(() => { if (tab === "history") loadHistory(); }, [tab, historyDate]);
  useEffect(() => { if (tab === "orders") { loadOrders(); const iv = setInterval(loadOrders, 15000); return () => clearInterval(iv); } }, [tab]);

  /* ── Menu actions ── */
  const saveMenuItem = async () => {
    if (!editingItem?.name?.trim()) { setMenuError("Name is required."); return; }
    setMenuError("");
    if (editMode === "create")
      await axios.post(`${API}/api/roomservice/menu`, editingItem, { headers });
    else
      await axios.put(`${API}/api/roomservice/menu/${editingItem.id}`, editingItem, { headers });
    setEditingItem(null); loadMenu();
  };
  const toggleAvailable = async (item: MenuItem) => {
    await axios.put(`${API}/api/roomservice/menu/${item.id}`, { available: !item.available }, { headers });
    loadMenu();
  };
  const deleteMenuItem = async (id: number) => {
    if (!confirm("Delete this menu item?")) return;
    await axios.delete(`${API}/api/roomservice/menu/${id}`, { headers });
    loadMenu();
  };

  const filteredMenu = menu.filter(m => {
    const matchCat = menuCat === "All" || m.category === menuCat;
    const matchQ   = !menuSearch || m.name.toLowerCase().includes(menuSearch.toLowerCase());
    return matchCat && matchQ;
  });
  const filteredHistory = history.filter(o =>
    !historySearch ||
    o.room_number.includes(historySearch) ||
    o.guest_name?.toLowerCase().includes(historySearch.toLowerCase()) ||
    o.items?.some(i => i.name.toLowerCase().includes(historySearch.toLowerCase()))
  );

  const liveCount = orders.filter(o => o.status === "pending" || o.status === "preparing").length;

  const tabs = [
    { key: "analytics", label: "Analytics",    icon: BarChart3 },
    { key: "orders",    label: "Live Orders",   icon: Package,        badge: liveCount },
    { key: "new-order", label: "New Order",     icon: Plus },
    { key: "history",   label: "Order History", icon: History },
    { key: "menu",      label: "Menu",          icon: UtensilsCrossed },
  ];

  const filteredOrders = orders.filter(o => orderFilter === "all" || o.status === orderFilter);

  return (
    <DashboardLayout>
      <div className="rsm-page">

        {/* Header */}
        <div className="rsm-header">
          <div>
            <h1 className="rsm-title">Room Service Manager</h1>
            <p className="rsm-subtitle">{new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</p>
          </div>
          <div className="rsm-header-right">
            <button className="rsm-refresh-btn" onClick={() => { if (tab === "analytics") loadAnalytics(); if (tab === "menu") loadMenu(); if (tab === "history") loadHistory(); if (tab === "orders") loadOrders(); }}>
              <RefreshCw size={14} /> Refresh
            </button>
            {tab === "menu" && (
              <button className="rsm-btn-primary" onClick={() => { setEditingItem({ name: "", category: "Main", price: 0, available: true, ingredients: [], unit: "serving" }); setEditMode("create"); }}>
                <Plus size={14} /> Add Item
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="rsm-tabs">
          {tabs.map(t => (
            <button key={t.key} className={`rsm-tab ${tab === t.key ? "active" : ""}`} onClick={() => setTab(t.key)}>
              <t.icon size={14} /> {t.label}
              {"badge" in t && (t as any).badge > 0 && (
                <span className="rsm-tab-badge">{(t as any).badge}</span>
              )}
            </button>
          ))}
        </div>

        {/* ══ NEW ORDER ══ */}
        {tab === "new-order" && (
          <NewOrderFlow onOrderPlaced={loadOrders} onViewOrders={() => setTab("orders")} />
        )}

        {/* ══ LIVE ORDERS ══ */}
        {tab === "orders" && (
          <>
            <div className="rsm-history-controls">
              {["all","pending","preparing","delivered","cancelled"].map(s => (
                <button key={s} className={`rsm-btn-ghost ${orderFilter === s ? "active" : ""}`} onClick={() => setOrderFilter(s)}>
                  {s === "all" ? `All (${orders.length})` : STATUS_META[s]?.label || s}
                </button>
              ))}
            </div>
            {ordersLoading
              ? <div className="rsm-loading"><span className="rsm-spinner" /> Loading orders…</div>
              : filteredOrders.length === 0
              ? <div className="rsm-empty"><Package size={32} /><p>No {orderFilter === "all" ? "" : orderFilter} orders</p></div>
              : (
                <div className="rsm-history-list">
                  {filteredOrders.map(order => {
                    const sm = STATUS_META[order.status] || STATUS_META.pending;
                    return (
                      <div key={order.id} className="rsm-history-row">
                        <div className="rsm-history-room"><BedDouble size={13} /> Room {order.room_number}</div>
                        {order.guest_name && <div className="rsm-history-guest">{order.guest_name}</div>}
                        <div className="rsm-history-time">{fmt(order.created_at)}</div>
                        <div className="rsm-history-items">
                          {order.items?.map((it, i) => (
                            <span key={i} className="rsm-item-chip">{it.qty}× {it.name}</span>
                          ))}
                        </div>
                        <span className="rsm-status-badge" style={{ background: sm.bg, color: sm.color }}>{sm.label}</span>
                        <span className="rsm-history-total">EGP {Number(order.total).toLocaleString()}</span>
                        <div className="rsm-row-actions">
                          {order.status === "pending"   && <button className="rsm-action-btn edit" onClick={() => updateOrderStatus(order.id, "preparing")}><ChefHat size={13} /> Prepare</button>}
                          {order.status === "preparing" && <button className="rsm-action-btn edit" onClick={() => updateOrderStatus(order.id, "delivered")}><CheckCircle2 size={13} /> Delivered</button>}
                          {(order.status === "pending" || order.status === "preparing") && (
                            <button className="rsm-action-btn del" onClick={() => updateOrderStatus(order.id, "cancelled")}><XCircle size={13} /></button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            }
          </>
        )}

        {/* ══ ANALYTICS ══ */}
        {tab === "analytics" && (
          <>
            {analyticsLoading || !analytics
              ? <div className="rsm-loading"><span className="rsm-spinner" /> Loading analytics…</div>
              : (
                <div className="rsm-analytics">

                  {/* KPIs */}
                  <div className="rsm-kpis">
                    {[
                      { label: "7-Day Revenue", value: `EGP ${analytics.totalRevenue7d.toLocaleString()}`, color: "#34d399", icon: TrendingUp },
                      { label: "7-Day Orders",  value: analytics.totalOrders7d, color: "#818cf8", icon: Package },
                      { label: "Avg Delivery",  value: analytics.avgDeliveryTime != null ? `${analytics.avgDeliveryTime} min` : "—", color: analytics.avgDeliveryTime && analytics.avgDeliveryTime > 25 ? "#f87171" : "#fbbf24", icon: Clock },
                      { label: "Cancellation",  value: `${analytics.cancellationRate}%`, color: analytics.cancellationRate > 15 ? "#f87171" : "#64748b", icon: XCircle },
                    ].map(k => (
                      <div key={k.label} className="rsm-kpi-card">
                        <k.icon size={16} style={{ color: k.color }} />
                        <span className="rsm-kpi-value" style={{ color: k.color }}>{k.value}</span>
                        <span className="rsm-kpi-label">{k.label}</span>
                      </div>
                    ))}
                  </div>

                  {/* Daily revenue */}
                  <div className="rsm-chart-card rsm-full">
                    <div className="rsm-chart-title"><TrendingUp size={14} /> Revenue — Last 7 Days</div>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={analytics.dailyRevenue} margin={{ left: 0, right: 10 }}>
                        <XAxis dataKey="date" tick={{ fill: "#475569", fontSize: 11 }} />
                        <YAxis tick={{ fill: "#475569", fontSize: 11 }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                        <Tooltip formatter={(v: any) => [`EGP ${Number(v).toLocaleString()}`, "Revenue"]} contentStyle={{ background: "#0f1729", border: "1px solid rgba(99,102,241,0.2)", borderRadius: 8, color: "rgba(201,168,76,0.07)", fontSize: 12 }} />
                        <Bar dataKey="revenue" fill="#6366f1" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Top items */}
                  <div className="rsm-chart-card">
                    <div className="rsm-chart-title"><Star size={14} /> Top 5 Items</div>
                    <div className="rsm-top-items">
                      {analytics.topItems.map((item, i) => (
                        <div key={item.name} className="rsm-top-row">
                          <span className="rsm-top-rank">#{i + 1}</span>
                          <span className="rsm-top-name">{item.name}</span>
                          <span className="rsm-top-count">{item.count}×</span>
                          <span className="rsm-top-rev">EGP {item.revenue.toLocaleString()}</span>
                          <div className="rsm-top-bar-wrap">
                            <div className="rsm-top-bar" style={{ width: `${(item.count / (analytics.topItems[0]?.count || 1)) * 100}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Revenue by category */}
                  <div className="rsm-chart-card">
                    <div className="rsm-chart-title"><BarChart3 size={14} /> Revenue by Category</div>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={analytics.revenueByCategory} layout="vertical" margin={{ left: 0, right: 20 }}>
                        <XAxis type="number" tick={{ fill: "#475569", fontSize: 11 }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                        <YAxis type="category" dataKey="category" width={80} tick={{ fill: "#64748b", fontSize: 12 }} />
                        <Tooltip formatter={(v: any) => [`EGP ${Number(v).toLocaleString()}`, "Revenue"]} contentStyle={{ background: "#0f1729", border: "1px solid rgba(99,102,241,0.2)", borderRadius: 8, color: "rgba(201,168,76,0.07)", fontSize: 12 }} />
                        <Bar dataKey="revenue" radius={[0, 6, 6, 0]}>
                          {analytics.revenueByCategory.map((_, i) => (
                            <Cell key={i} fill={["#6366f1","#8b5cf6","#06b6d4","#34d399","#fbbf24","#f87171"][i % 6]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Peak hours */}
                  <div className="rsm-chart-card rsm-full">
                    <div className="rsm-chart-title"><Clock size={14} /> Peak Hours</div>
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={analytics.peakHours} margin={{ left: 0, right: 10 }}>
                        <XAxis dataKey="hour" tick={{ fill: "#475569", fontSize: 11 }} tickFormatter={h => `${h}:00`} />
                        <YAxis tick={{ fill: "#475569", fontSize: 11 }} allowDecimals={false} />
                        <Tooltip formatter={(v: any) => [v, "Orders"]} labelFormatter={h => `${h}:00`} contentStyle={{ background: "#0f1729", border: "1px solid rgba(99,102,241,0.2)", borderRadius: 8, color: "rgba(201,168,76,0.07)", fontSize: 12 }} />
                        <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                </div>
              )
            }
          </>
        )}

        {/* ══ MENU ══ */}
        {tab === "menu" && (
          <>
            <div className="rsm-menu-controls">
              <div className="rsm-search">
                <Search size={14} />
                <input placeholder="Search menu…" value={menuSearch} onChange={e => setMenuSearch(e.target.value)} />
                {menuSearch && <button onClick={() => setMenuSearch("")}><X size={13} /></button>}
              </div>
              <div className="rsm-cat-tabs">
                {["All", ...CATEGORIES].map(c => (
                  <button key={c} className={`rsm-cat-tab ${menuCat === c ? "active" : ""}`} onClick={() => setMenuCat(c)}>{c}</button>
                ))}
              </div>
            </div>

            {filteredMenu.length === 0
              ? <div className="rsm-empty"><UtensilsCrossed size={32} /><p>No items</p></div>
              : (
                <div className="rsm-menu-table">
                  <div className="rsm-table-head">
                    <span>Item</span><span>Category</span><span>Price</span><span>Available</span><span>Schedule</span><span></span>
                  </div>
                  {filteredMenu.map(item => (
                    <div key={item.id} className={`rsm-table-row ${!item.available ? "dim" : ""}`}>
                      <div className="rsm-item-name-cell">
                        {item.image_url && <img src={item.image_url} alt={item.name} className="rsm-table-img" />}
                        <div>
                          <div className="rsm-item-name">{item.name}</div>
                          {item.description && <div className="rsm-item-desc">{item.description}</div>}
                        </div>
                      </div>
                      <span className="rsm-cat-badge">{item.category}</span>
                      <span className="rsm-price">EGP {Number(item.price).toLocaleString()}</span>
                      <button className="rsm-toggle" onClick={() => toggleAvailable(item)}>
                        {item.available ? <ToggleRight size={22} color="#34d399" /> : <ToggleLeft size={22} color="#475569" />}
                      </button>
                      <span className="rsm-schedule">
                        {item.available_from ? `${item.available_from}–${item.available_to}` : "Always"}
                      </span>
                      <div className="rsm-row-actions">
                        <button className="rsm-action-btn edit" onClick={() => { setEditingItem({ ...item }); setEditMode("edit"); }}><Pencil size={13} /></button>
                        <button className="rsm-action-btn del" onClick={() => deleteMenuItem(item.id)}><Trash2 size={13} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            }
          </>
        )}

        {/* ══ HISTORY ══ */}
        {tab === "history" && (
          <>
            <div className="rsm-history-controls">
              <input type="date" className="rsm-date-input" value={historyDate} onChange={e => setHistoryDate(e.target.value)} />
              {historyDate !== todayISO() && (
                <button className="rsm-btn-ghost" onClick={() => setHistoryDate(todayISO())}>Today</button>
              )}
              <div className="rsm-search" style={{ flex: 1, maxWidth: 280 }}>
                <Search size={14} />
                <input placeholder="Room, guest, item…" value={historySearch} onChange={e => setHistorySearch(e.target.value)} />
                {historySearch && <button onClick={() => setHistorySearch("")}><X size={13} /></button>}
              </div>
              <span className="rsm-history-count">{filteredHistory.length} orders</span>
            </div>

            {historyLoading
              ? <div className="rsm-loading"><span className="rsm-spinner" /> Loading…</div>
              : filteredHistory.length === 0
              ? <div className="rsm-empty"><History size={32} /><p>No orders for this date</p></div>
              : (
                <div className="rsm-history-list">
                  {filteredHistory.map(order => {
                    const sm = STATUS_META[order.status] || STATUS_META.pending;
                    return (
                      <div key={order.id} className="rsm-history-row">
                        <div className="rsm-history-room"><BedDouble size={13} /> Room {order.room_number}</div>
                        {order.guest_name && <div className="rsm-history-guest">{order.guest_name}</div>}
                        <div className="rsm-history-time">{fmt(order.created_at)}</div>
                        <div className="rsm-history-items">
                          {order.items?.map((it, i) => (
                            <span key={i} className="rsm-item-chip">{it.qty}× {it.name}</span>
                          ))}
                        </div>
                        <span className="rsm-status-badge" style={{ background: sm.bg, color: sm.color }}>{sm.label}</span>
                        <span className="rsm-history-total">EGP {Number(order.total).toLocaleString()}</span>
                      </div>
                    );
                  })}
                </div>
              )
            }
          </>
        )}

      </div>

      {/* ══ Menu Item Modal ══ */}
      {editingItem && (
        <div className="rsm-overlay" onClick={() => setEditingItem(null)}>
          <div className="rsm-modal" onClick={e => e.stopPropagation()}>
            <div className="rsm-modal-header">
              <h2>{editMode === "create" ? "Add Menu Item" : "Edit Menu Item"}</h2>
              <button onClick={() => setEditingItem(null)}><X size={18} /></button>
            </div>
            <div className="rsm-modal-body">
              <div className="rsm-form-row">
                <div className="rsm-form-group" style={{ flex: 2 }}>
                  <label>Name *</label>
                  <input value={editingItem.name || ""} onChange={e => setEditingItem(p => ({ ...p!, name: e.target.value }))} placeholder="e.g. Club Sandwich" />
                </div>
                <div className="rsm-form-group">
                  <label>Category</label>
                  <select value={editingItem.category || "Main"} onChange={e => setEditingItem(p => ({ ...p!, category: e.target.value }))}>
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="rsm-form-row">
                <div className="rsm-form-group">
                  <label>Price (EGP)</label>
                  <input type="number" min={0} value={editingItem.price || 0} onChange={e => setEditingItem(p => ({ ...p!, price: Number(e.target.value) }))} />
                </div>
                <div className="rsm-form-group">
                  <label>Unit</label>
                  <input value={editingItem.unit || "serving"} onChange={e => setEditingItem(p => ({ ...p!, unit: e.target.value }))} />
                </div>
                <div className="rsm-form-group">
                  <label>Available</label>
                  <button className="rsm-toggle" onClick={() => setEditingItem(p => ({ ...p!, available: !p!.available }))}>
                    {editingItem.available ? <ToggleRight size={28} color="#34d399" /> : <ToggleLeft size={28} color="#475569" />}
                  </button>
                </div>
              </div>
              <div className="rsm-form-row">
                <div className="rsm-form-group">
                  <label>Available From</label>
                  <input type="time" value={editingItem.available_from || ""} onChange={e => setEditingItem(p => ({ ...p!, available_from: e.target.value || undefined }))} />
                </div>
                <div className="rsm-form-group">
                  <label>Available To</label>
                  <input type="time" value={editingItem.available_to || ""} onChange={e => setEditingItem(p => ({ ...p!, available_to: e.target.value || undefined }))} />
                </div>
              </div>
              <div className="rsm-form-group">
                <label><Image size={11} style={{ display: "inline", marginRight: 4 }} />Image</label>
                <input type="file" accept="image/*" className="rsm-file-input" onChange={e => {
                  const file = e.target.files?.[0]; if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => setEditingItem(p => ({ ...p!, image_url: reader.result as string }));
                  reader.readAsDataURL(file);
                }} />
                {editingItem.image_url && (
                  <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                    <img src={editingItem.image_url} alt="preview" style={{ width: "100%", maxHeight: 100, objectFit: "cover", borderRadius: 8 }} />
                    <button className="rsm-img-remove" onClick={() => setEditingItem(p => ({ ...p!, image_url: undefined }))}><X size={11} /> Remove</button>
                  </div>
                )}
              </div>
              <div className="rsm-form-group">
                <label>Description</label>
                <textarea value={editingItem.description || ""} onChange={e => setEditingItem(p => ({ ...p!, description: e.target.value }))} rows={2} placeholder="Brief description…" />
              </div>
              <div className="rsm-form-group">
                <label>Ingredients</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input value={ingredientInput} onChange={e => setIngredientInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && ingredientInput.trim()) { setEditingItem(p => ({ ...p!, ingredients: [...(p!.ingredients || []), ingredientInput.trim()] })); setIngredientInput(""); }}}
                    placeholder="Type + Enter" style={{ flex: 1 }} />
                  <button className="rsm-btn-ghost" onClick={() => { if (!ingredientInput.trim()) return; setEditingItem(p => ({ ...p!, ingredients: [...(p!.ingredients || []), ingredientInput.trim()] })); setIngredientInput(""); }}>Add</button>
                </div>
                <div className="rsm-ing-tags">
                  {(editingItem.ingredients || []).map((ing, i) => (
                    <span key={i} className="rsm-ing-tag">{ing}
                      <button onClick={() => setEditingItem(p => ({ ...p!, ingredients: p!.ingredients!.filter((_, j) => j !== i) }))}><X size={10} /></button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="rsm-modal-footer">
              {menuError && <span className="rsm-error">{menuError}</span>}
              <button className="rsm-btn-ghost" onClick={() => { setEditingItem(null); setMenuError(""); }}>Cancel</button>
              <button className="rsm-btn-primary" onClick={saveMenuItem}>{editMode === "create" ? "Add to Menu" : "Save Changes"}</button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
